/*
 * Air Premium (2026-10-05) - Horizon size selector.
 *
 * Ported from Horizon (theme 166910099634):
 *   assets/variant-picker.js  connectedCallback (radio / checked-index bookkeeping), variantChanged,
 *                             updateSelectedOption, #getFieldsetMeasurements, #applyFieldsetMeasurements,
 *                             updateFieldsetCss, updateVariantPickerCss
 *   assets/utilities.js       ResizeNotifier
 * These drive Horizon's selected state and the sliding "pill" (data-current-checked /
 * data-previous-checked, --pill-width-current / --pill-width-previous).
 *
 * Not ported: Horizon's fetchUpdatedSection / morph (it re-renders a Horizon section). In Air Premium
 * the variant is still resolved by Kalles (swatch.min.js): each change here is forwarded as a click on
 * the matching hidden Kalles .nt-swatch, which updates select[name=id], price, stock, images and the
 * Add to cart / Buy it now buttons exactly as before. Kalles state is mirrored back so both stay in sync.
 */
(() => {
  if (!window.customElements || customElements.get('variant-picker')) return;

  /** Horizon utilities.js ResizeNotifier: skips the initial observe callback. */
  class ResizeNotifier extends ResizeObserver {
    #initialized = false;

    constructor(callback) {
      super((entries) => {
        if (this.#initialized) return callback(entries, this);
        this.#initialized = true;
      });
    }

    disconnect() {
      this.#initialized = false;
      super.disconnect();
    }
  }

  class VariantPicker extends HTMLElement {
    /** @type {number[][]} */
    #checkedIndices = [];

    /** @type {HTMLInputElement[][]} */
    #radios = [];

    #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

    /** @type {MutationObserver | undefined} */
    #kallesObserver;

    #retryTimer = 0;

    get fieldsets() {
      return Array.from(this.querySelectorAll('[ref="fieldsets[]"]'));
    }

    get kalles() {
      return document.getElementById(this.dataset.kallesId || '');
    }

    connectedCallback() {
      this.fieldsets.forEach((fieldset) => {
        const radios = Array.from(fieldset.querySelectorAll('input'));
        this.#radios.push(radios);

        const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
        // Horizon always starts with a checked value. Kalles (settings.pr_curent) can start with none,
        // so an empty history is kept per fieldset to let the pill bookkeeping start on the first click.
        this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
      });

      this.addEventListener('change', this.variantChanged.bind(this));
      this.#resizeObserver.observe(this);

      const kalles = this.kalles;
      if (kalles) {
        this.#kallesObserver = new MutationObserver(() => this.#syncFromKalles());
        this.#kallesObserver.observe(kalles, { subtree: true, attributes: true, attributeFilter: ['class'] });
        this.#syncFromKalles();
      }
    }

    disconnectedCallback() {
      this.#resizeObserver.disconnect();
      this.#kallesObserver?.disconnect();
      clearTimeout(this.#retryTimer);
    }

    /**
     * Horizon variantChanged, minus the section fetch: update the UI, then let Kalles select the variant.
     * @param {Event} event
     */
    variantChanged(event) {
      if (!(event.target instanceof HTMLInputElement)) return;

      this.updateSelectedOption(event.target);
      this.#selectInKalles(event.target);
    }

    /** Batch all reads first across all fieldsets to avoid layout thrashing (Horizon). */
    updateVariantPickerCss() {
      const measurements = this.fieldsets
        .map((_, index) => this.#getFieldsetMeasurements(index))
        .filter((m) => m !== null);

      for (const measurement of measurements) {
        this.#applyFieldsetMeasurements(measurement);
      }
    }

    /** @param {number} fieldsetIndex */
    #getFieldsetMeasurements(fieldsetIndex) {
      const fieldset = this.fieldsets[fieldsetIndex];
      const checkedIndices = this.#checkedIndices[fieldsetIndex];
      const radios = this.#radios[fieldsetIndex];

      if (!radios || !checkedIndices || !fieldset) return null;

      const [currentIndex, previousIndex] = checkedIndices;

      return {
        fieldset,
        currentIndex,
        previousIndex,
        currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
        previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
      };
    }

    #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
      if (currentWidth) {
        fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
      } else if (currentIndex !== undefined) {
        fieldset.style.removeProperty('--pill-width-current');
      }

      if (previousWidth) {
        fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
      } else if (previousIndex !== undefined) {
        fieldset.style.removeProperty('--pill-width-previous');
      }
    }

    /** @param {number} fieldsetIndex */
    updateFieldsetCss(fieldsetIndex) {
      if (Number.isNaN(fieldsetIndex)) return;

      const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
      if (measurements) {
        this.#applyFieldsetMeasurements(measurements);
      }
    }

    /**
     * Horizon updateSelectedOption (radio branch).
     * @param {HTMLInputElement} target
     */
    updateSelectedOption(target) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldset = this.fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    /** @param {HTMLInputElement} input */
    #kallesSwatchFor(input) {
      const fieldset = input.closest('[ref="fieldsets[]"]');
      const optionIndex = fieldset?.getAttribute('data-ap-option-index');
      const list = this.kalles?.querySelector(`.swatches-select[data-id="${optionIndex}"]`);
      return list?.children[Number.parseInt(input.dataset.inputIndex || '')] || null;
    }

    /**
     * Click the matching Kalles swatch. Kalles binds its handler after it loads the product JSON,
     * so the click is retried until Kalles has marked the swatch as selected.
     * @param {HTMLInputElement} input
     */
    #selectInKalles(input, attempt = 0) {
      clearTimeout(this.#retryTimer);
      const swatch = this.#kallesSwatchFor(input);
      if (!swatch || swatch.classList.contains('is-selected') || !input.checked) return;

      swatch.click();

      if (!swatch.classList.contains('is-selected') && attempt < 50) {
        this.#retryTimer = window.setTimeout(() => this.#selectInKalles(input, attempt + 1), 150);
      }
    }

    /** Mirror the Kalles selection (e.g. selected by Kalles itself) into the Horizon radios. */
    #syncFromKalles() {
      this.fieldsets.forEach((fieldset, fieldsetIndex) => {
        const optionIndex = fieldset.getAttribute('data-ap-option-index');
        const list = this.kalles?.querySelector(`.swatches-select[data-id="${optionIndex}"]`);
        if (!list) return;

        const items = Array.from(list.children);
        const selectedIndex = items.findIndex(
          (item) => item.classList.contains('is-selected') || item.classList.contains('is-selected-nt')
        );
        const radio = this.#radios[fieldsetIndex]?.[selectedIndex];

        if (radio && !radio.checked) this.updateSelectedOption(radio);
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
})();
